import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Copy, Check, Search, UserPlus, Users, Crown, Clock, X, UserCheck, UserX, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import UserAvatar from "@/components/UserAvatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

interface Friendship {
  id: string; user_id: string; friend_id: string; status: string; created_at: string;
  friend_username?: string; friend_is_premium?: boolean; friend_premium_type?: string | null; friend_avatar_url?: string | null;
}

const Friends = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, username, isPremium, premiumType, avatarUrl } = useAuth();
  const [copied, setCopied] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<{ id: string; username: string; is_premium: boolean; premium_type: string | null; avatar_url: string | null }[]>([]);
  const [searching, setSearching] = useState(false);
  const [friends, setFriends] = useState<Friendship[]>([]);
  const [pendingReceived, setPendingReceived] = useState<Friendship[]>([]);
  const [pendingSent, setPendingSent] = useState<Friendship[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingRequest, setSendingRequest] = useState<string | null>(null);
  const [selectedFriend, setSelectedFriend] = useState<Friendship | null>(null);
  const [tab, setTab] = useState<"friends" | "requests">("friends");

  const fetchFriendships = useCallback(async () => {
    if (!user) return;
    const { data, error } = await (supabase.from("friendships") as any).select("*").or(`user_id.eq.${user.id},friend_id.eq.${user.id}`);
    if (error) { console.error(error); return; }
    const accepted: Friendship[] = []; const receivedPending: Friendship[] = []; const sentPending: Friendship[] = [];
    const otherIds = new Set<string>();
    (data || []).forEach((f: any) => { otherIds.add(f.user_id === user.id ? f.friend_id : f.user_id); });
    let profileMap: Record<string, { username: string; is_premium: boolean; premium_type: string | null; avatar_url: string | null }> = {};
    if (otherIds.size > 0) {
      const { data: profiles } = await supabase.rpc("get_public_profiles", { _user_ids: Array.from(otherIds) });
      (profiles || []).forEach((p: any) => { profileMap[p.id] = { username: p.username || "Unknown", is_premium: p.is_premium, premium_type: p.premium_type, avatar_url: p.avatar_url }; });
    }
    (data || []).forEach((f: any) => {
      const otherId = f.user_id === user.id ? f.friend_id : f.user_id;
      const enriched: Friendship = { ...f, friend_username: profileMap[otherId]?.username || "Unknown", friend_is_premium: profileMap[otherId]?.is_premium || false, friend_premium_type: profileMap[otherId]?.premium_type || null, friend_avatar_url: profileMap[otherId]?.avatar_url || null };
      if (f.status === "accepted") accepted.push(enriched);
      else if (f.status === "pending") { if (f.friend_id === user.id) receivedPending.push(enriched); else sentPending.push(enriched); }
    });
    setFriends(accepted); setPendingReceived(receivedPending); setPendingSent(sentPending); setLoading(false);
  }, [user]);

  useEffect(() => { fetchFriendships(); }, [fetchFriendships]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase.channel("friendships-page").on("postgres_changes", { event: "*", schema: "public", table: "friendships" }, () => { fetchFriendships(); }).subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, fetchFriendships]);

  const copyUsername = () => { if (!username) return; navigator.clipboard.writeText(username); setCopied(true); toast.success(t("friends.usernameCopied")); setTimeout(() => setCopied(false), 2000); };

  const searchUsers = async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) return;
    setSearching(true);
    try { const { data } = await supabase.rpc("search_profiles_by_username", { _query: searchQuery.trim(), _exclude_id: user?.id, _limit: 10 }); setSearchResults(data || []); } catch { toast.error(t("common.error")); } finally { setSearching(false); }
  };

  const sendFriendRequest = async (friendId: string) => {
    if (!user) return; setSendingRequest(friendId);
    try {
      const { data: existing } = await (supabase.from("friendships") as any).select("id, status").or(`and(user_id.eq.${user.id},friend_id.eq.${friendId}),and(user_id.eq.${friendId},friend_id.eq.${user.id})`).maybeSingle();
      if (existing) { toast.info(existing.status === "accepted" ? t("friends.alreadyFriends") : t("friends.requestAlreadySent")); return; }
      const { error } = await (supabase.from("friendships") as any).insert({ user_id: user.id, friend_id: friendId, status: "pending" });
      if (error) throw error;
      toast.success(t("friends.requestSent")); setSearchQuery(""); setSearchResults([]); fetchFriendships();
    } catch (e: any) { toast.error(e.message || t("common.error")); } finally { setSendingRequest(null); }
  };

  const respondToRequest = async (friendshipId: string, accept: boolean) => {
    try {
      if (accept) { await (supabase.from("friendships") as any).update({ status: "accepted", updated_at: new Date().toISOString() }).eq("id", friendshipId); toast.success(t("friends.accepted")); }
      else { await (supabase.from("friendships") as any).delete().eq("id", friendshipId); toast.success(t("friends.declined")); }
      fetchFriendships();
    } catch { toast.error(t("common.error")); }
  };

  const removeFriend = async (friendshipId: string) => {
    try { await (supabase.from("friendships") as any).delete().eq("id", friendshipId); toast.success(t("friends.removed")); setSelectedFriend(null); fetchFriendships(); } catch { toast.error(t("common.error")); }
  };

  const startGroupWithFriend = (friend: Friendship) => {
    setSelectedFriend(null);
    navigate("/discover", { state: { startGroup: true, inviteFriendId: friend.friend_id === user?.id ? friend.user_id : friend.friend_id, inviteFriendUsername: friend.friend_username } });
  };

  if (!user) {
    return (
      <div className="min-h-[100dvh] bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <Users className="w-12 h-12 text-muted-foreground mx-auto" />
          <h2 className="font-display text-xl font-bold text-foreground">{t("friends.signInToAdd")}</h2>
          <p className="text-sm text-muted-foreground">{t("friends.signInToAddDesc")}</p>
          <button onClick={() => navigate("/auth")} className="px-6 py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold shadow-iris">{t("common.signIn")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-background pb-16">
      <div className="px-4 pt-6 pb-4">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/home")} className="p-2 rounded-xl bg-secondary"><ArrowLeft className="w-5 h-5 text-foreground" /></button>
          <h1 className="font-display text-xl font-bold text-foreground">{t("friends.title")}</h1>
        </div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-card border border-border rounded-2xl p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <UserAvatar avatarUrl={avatarUrl} username={username} isPremium={isPremium} premiumType={premiumType} size="lg" />
              <div>
                <p className="font-display font-semibold text-foreground">@{username}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {isPremium ? (<span className="flex items-center gap-1 text-xs text-star-gold"><Crown className="w-3 h-3" /> {t("common.premium")}</span>) : (<span className="text-xs text-muted-foreground">{t("common.freePlan")}</span>)}
                </div>
              </div>
            </div>
            <button onClick={copyUsername} className="p-2.5 rounded-xl bg-secondary hover:bg-muted transition-colors">
              {copied ? <Check className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
            </button>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-6">
          <label className="text-xs text-muted-foreground tracking-wider mb-2 block">{t("friends.addFriend")}</label>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchUsers()} placeholder={t("friends.searchByUsername")} className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" />
            </div>
            <button onClick={searchUsers} disabled={searching || searchQuery.trim().length < 2} className="px-4 py-2.5 rounded-xl bg-gradient-iris text-primary-foreground font-medium text-sm shadow-iris disabled:opacity-50 active:scale-95 transition-transform">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            </button>
          </div>
          <AnimatePresence>
            {searchResults.length > 0 && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mt-2 space-y-1 overflow-hidden">
                {searchResults.map((result) => (
                  <div key={result.id} className="flex items-center justify-between p-3 rounded-xl bg-secondary border border-border">
                    <div className="flex items-center gap-2">
                      <UserAvatar avatarUrl={result.avatar_url} username={result.username} isPremium={result.is_premium} premiumType={result.premium_type} size="sm" />
                      <div><p className="text-sm font-medium text-foreground">@{result.username}</p>{result.is_premium && (<span className="flex items-center gap-1 text-[10px] text-star-gold"><Crown className="w-2.5 h-2.5" /> {t("common.premium")}</span>)}</div>
                    </div>
                    <button onClick={() => sendFriendRequest(result.id)} disabled={sendingRequest === result.id} className="px-3 py-1.5 rounded-lg bg-primary/15 text-primary text-xs font-medium hover:bg-primary/25 transition-colors disabled:opacity-50">
                      {sendingRequest === result.id ? <Loader2 className="w-3 h-3 animate-spin" /> : t("friends.add")}
                    </button>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <div className="flex gap-2 mb-4">
          <button onClick={() => setTab("friends")} className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors ${tab === "friends" ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"}`}>{t("friends.friendsTab")} ({friends.length})</button>
          <button onClick={() => setTab("requests")} className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors relative ${tab === "requests" ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"}`}>
            {t("friends.requestsTab")}
            {pendingReceived.length > 0 && (<span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">{pendingReceived.length}</span>)}
          </button>
        </div>

        {tab === "friends" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2">
            {loading ? (<div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground mx-auto" /></div>) : friends.length === 0 ? (
              <div className="text-center py-8"><Users className="w-10 h-10 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">{t("friends.noFriends")}</p></div>
            ) : (
              friends.map((friend) => (
                <button key={friend.id} onClick={() => setSelectedFriend(friend)} className="w-full flex items-center gap-3 p-3 rounded-xl bg-secondary border border-border hover:border-primary/30 transition-colors text-left active:scale-[0.98]">
                  <UserAvatar avatarUrl={friend.friend_avatar_url} username={friend.friend_username} isPremium={friend.friend_is_premium} premiumType={friend.friend_premium_type} size="md" />
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium text-foreground truncate">@{friend.friend_username}</p>{friend.friend_is_premium && (<span className="flex items-center gap-1 text-[10px] text-star-gold"><Crown className="w-2.5 h-2.5" /> {t("common.premium")}</span>)}</div>
                  <Users className="w-4 h-4 text-muted-foreground" />
                </button>
              ))
            )}
          </motion.div>
        )}

        {tab === "requests" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            {pendingReceived.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground tracking-wider mb-2">{t("friends.received")}</p>
                <div className="space-y-2">
                  {pendingReceived.map((req) => (
                    <div key={req.id} className="flex items-center gap-3 p-3 rounded-xl bg-secondary border border-border">
                      <UserAvatar avatarUrl={req.friend_avatar_url} username={req.friend_username} isPremium={req.friend_is_premium} premiumType={req.friend_premium_type} size="md" />
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium text-foreground truncate">@{req.friend_username}</p><p className="text-[10px] text-muted-foreground">{t("friends.wantsToBeFriends")}</p></div>
                      <div className="flex gap-1.5">
                        <button onClick={() => respondToRequest(req.id, true)} className="p-2 rounded-lg bg-primary/15 text-primary hover:bg-primary/25 transition-colors"><UserCheck className="w-4 h-4" /></button>
                        <button onClick={() => respondToRequest(req.id, false)} className="p-2 rounded-lg bg-destructive/15 text-destructive hover:bg-destructive/25 transition-colors"><UserX className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {pendingSent.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground tracking-wider mb-2">{t("friends.sent")}</p>
                <div className="space-y-2">
                  {pendingSent.map((req) => (
                    <div key={req.id} className="flex items-center gap-3 p-3 rounded-xl bg-secondary border border-border">
                      <UserAvatar avatarUrl={req.friend_avatar_url} username={req.friend_username} isPremium={req.friend_is_premium} premiumType={req.friend_premium_type} size="md" />
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium text-foreground truncate">@{req.friend_username}</p><p className="text-[10px] text-muted-foreground">{t("friends.pending")}</p></div>
                      <Clock className="w-4 h-4 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {pendingReceived.length === 0 && pendingSent.length === 0 && (
              <div className="text-center py-8"><p className="text-sm text-muted-foreground">{t("common.noResults")}</p></div>
            )}
          </motion.div>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground/60 text-center py-4">© 2026 Ayeris.tv — All rights reserved.</p>
      <Dialog open={!!selectedFriend} onOpenChange={(open) => { if (!open) setSelectedFriend(null); }}>
        <DialogContent className="max-w-[320px] rounded-2xl">
          <DialogHeader>
            <DialogTitle>{t("friends.friendActions")}</DialogTitle>
            <DialogDescription className="sr-only">{t("friends.friendActions")}</DialogDescription>
          </DialogHeader>
          {selectedFriend && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-secondary">
                <UserAvatar avatarUrl={selectedFriend.friend_avatar_url} username={selectedFriend.friend_username} isPremium={selectedFriend.friend_is_premium} premiumType={selectedFriend.friend_premium_type} size="md" />
                <div><p className="font-medium text-foreground">@{selectedFriend.friend_username}</p>{selectedFriend.friend_is_premium && (<span className="flex items-center gap-1 text-[10px] text-star-gold"><Crown className="w-2.5 h-2.5" /> {t("common.premium")}</span>)}</div>
              </div>
              <button onClick={() => startGroupWithFriend(selectedFriend)} className="w-full py-3 rounded-xl bg-gradient-iris text-primary-foreground font-semibold text-sm shadow-iris active:scale-95 transition-transform flex items-center justify-center gap-2"><Users className="w-4 h-4" /> {t("friends.startGroup")}</button>
              <button onClick={() => removeFriend(selectedFriend.id)} className="w-full py-3 rounded-xl bg-destructive/10 text-destructive font-medium text-sm hover:bg-destructive/20 transition-colors active:scale-95 flex items-center justify-center gap-2"><X className="w-4 h-4" /> {t("friends.removeFriend")}</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Friends;
